"use client";

import React from "react";
import Link from "next/link";
import { MapPin, Phone, Mail, ArrowUp } from "lucide-react";

interface LandingFooterProps {
  onOpenDemo: () => void;
  onOpenLegal: (doc: "privacy" | "terms" | "safeguarding" | "copyright") => void;
}

export function LandingFooter({ onOpenDemo, onOpenLegal }: LandingFooterProps) {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#F7F7F5] text-[#1A3C2B] border-t border-[#3A3A38]/30 pt-16 pb-12 relative overflow-hidden font-mono-tech">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-12 border-b border-[#3A3A38]/20">
          {/* Col 1 & 2: Brand Identity */}
          <div className="lg:col-span-2 space-y-4">
            <Link href="/" className="flex items-center gap-4 group">
              <div className="w-16 h-16 sm:w-20 sm:h-20 border-2 border-[#1A3C2B] bg-white overflow-hidden flex items-center justify-center shrink-0 shadow-md">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/media/olympio_logo.jpeg.png"
                  alt="Olympio Logo"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-space font-bold text-3xl sm:text-4xl text-[#1A3C2B] tracking-tight uppercase">
                  OLYMPIO
                </span>
                <span className="text-[10px] sm:text-[11px] text-[#3A3A38]/80 uppercase tracking-widest -mt-1 font-bold">
                  SYSTEMS INC. // RACE TO OLYMPICS
                </span>
              </div>
            </Link>

            <p className="text-xs text-[#3A3A38] leading-relaxed max-w-sm">
              The Intelligent School Sports ERP, AI Biometric Youth Talent Scouting Radar &amp; After-Hours Campus Sports Facility Monetization OS.
            </p>

            <div className="space-y-1.5 text-[11px] text-[#3A3A38] pt-1">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-[#1A3C2B] shrink-0" />
                <span>TechZone 4, Greater Noida West, NCR, India</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-[#1A3C2B] shrink-0" />
                <span>+91 9958382933 | +91 7051594695 | +91 9625825683</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-[#1A3C2B] shrink-0" />
                <span>Olympiocampus@gmail.com · CEO@olympio.in</span>
              </div>
            </div>
          </div>

          {/* Col 3: Architecture & Science */}
          <div className="space-y-3">
            <h4 className="font-space font-bold text-[#1A3C2B] text-sm uppercase tracking-wider">
              01. ECOSYSTEM
            </h4>
            <ul className="space-y-2 text-xs text-[#3A3A38] uppercase">
              <li><a href="#dual-engine" className="hover:text-[#1A3C2B] underline-offset-4 hover:underline">Dual-Engine Architecture</a></li>
              <li><a href="#science" className="hover:text-[#1A3C2B] underline-offset-4 hover:underline">Eurofit 6-Station Science</a></li>
              <li><a href="#science" className="hover:text-[#1A3C2B] underline-offset-4 hover:underline">Mirwald PHV Maturation</a></li>
              <li><a href="#modules" className="hover:text-[#1A3C2B] underline-offset-4 hover:underline">4 Bento Core Engines</a></li>
              <li><Link href="/explore" className="hover:text-[#1A3C2B] underline-offset-4 hover:underline">Public Facility Rentals</Link></li>
            </ul>
          </div>

          {/* Col 4: Portals */}
          <div className="space-y-3">
            <h4 className="font-space font-bold text-[#1A3C2B] text-sm uppercase tracking-wider">
              02. PORTALS
            </h4>
            <ul className="space-y-2 text-xs text-[#3A3A38] uppercase">
              <li><Link href="/login" className="hover:text-[#1A3C2B] underline-offset-4 hover:underline">Principal Command ERP</Link></li>
              <li><Link href="/login" className="hover:text-[#1A3C2B] underline-offset-4 hover:underline">PE Teacher &amp; Coach Console</Link></li>
              <li><Link href="/login" className="hover:text-[#1A3C2B] underline-offset-4 hover:underline">Parent Biometric Passport</Link></li>
              <li><Link href="/login" className="hover:text-[#1A3C2B] underline-offset-4 hover:underline">Student AI Coach Hub</Link></li>
            </ul>
          </div>

          {/* Col 5: Security & Compliance */}
          <div className="space-y-3">
            <h4 className="font-space font-bold text-[#1A3C2B] text-sm uppercase tracking-wider">
              03. GOVERNANCE
            </h4>
            <div className="border border-[#3A3A38]/30 bg-white p-3 space-y-2 rounded-none">
              <span className="text-[10px] font-bold text-[#1A3C2B] block uppercase">
                DPDP ACT 2023 VERIFIED
              </span>
              <p className="text-[10px] text-[#3A3A38] leading-tight">
                AES-256 encrypted biometric consent vault &amp; zero-trust campus security.
              </p>
              <button
                onClick={onOpenDemo}
                className="w-full py-1.5 bg-[#1A3C2B] text-white text-[10px] uppercase font-bold rounded-none"
              >
                REQUEST SECURITY WHITEPAPER
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Copyright Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#3A3A38] pt-4 uppercase">
          <div>
            &copy; {new Date().getFullYear()} OLYMPIO PVT. LTD. // ALL RIGHTS RESERVED.
          </div>

          <div className="flex flex-wrap items-center gap-4 text-[10px]">
            <button onClick={() => onOpenLegal("privacy")} className="hover:text-[#1A3C2B]">Privacy Policy</button>
            <span>•</span>
            <button onClick={() => onOpenLegal("terms")} className="hover:text-[#1A3C2B]">Terms of Service</button>
            <span>•</span>
            <button onClick={() => onOpenLegal("safeguarding")} className="hover:text-[#1A3C2B]">Child Safeguarding</button>
            <span>•</span>
            <button onClick={scrollToTop} className="p-1 border border-[#1A3C2B] text-[#1A3C2B] hover:bg-[#1A3C2B] hover:text-white transition">
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
